// Opens Tidio from the header “Chat with a Shelf Geek!” button.
(() => {
  if (document.documentElement.dataset.shelfologyLiveChat === 'true') return;
  document.documentElement.dataset.shelfologyLiveChat = 'true';

  const BUTTON_SELECTOR = '[data-open-live-chat]';
  const MESSAGE_FIELD_ID = 'new-message-textarea';
  const OPEN_CLAIM_MS = 2000;

  let returnButton = null;
  let expectingOpen = false;
  let openedByHeader = false;
  let widgetOpen = false;
  let focusToken = 0;
  let tidioReady = false;
  let claimTimer = 0;
  let apiEventsBound = false;

  function chatApi() {
    const api = window.tidioChatApi;
    return api && typeof api.open === 'function' ? api : null;
  }

  function isVisible(element) {
    if (!element || element.disabled) return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    if (element.closest('[aria-hidden="true"]')) return false;

    const view = element.ownerDocument.defaultView;
    const style = view.getComputedStyle(element);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    return element.getClientRects().length > 0;
  }

  function widgetHost() {
    return document.getElementById('tidio-chat-iframe') || document.getElementById('tidio-chat');
  }

  function widgetScope() {
    const iframe = document.getElementById('tidio-chat-iframe');
    if (iframe) {
      try {
        if (iframe.contentDocument) return iframe.contentDocument;
      } catch (error) {
        return null;
      }
    }

    const host = document.getElementById('tidio-chat');
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  function deepActive(root) {
    let active = root && root.activeElement;
    while (active && active.shadowRoot && active.shadowRoot.activeElement) {
      active = active.shadowRoot.activeElement;
    }
    return active || null;
  }

  function focusElement(element) {
    if (!element || typeof element.focus !== 'function') return false;
    element.focus();
    return deepActive(element.ownerDocument) === element;
  }

  function chatTab(scope) {
    return [...scope.querySelectorAll('button')].find((button) => {
      if (button.id === 'button-body') return false;
      const label = (button.innerText || button.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim();
      return /^chat$/i.test(label);
    });
  }

  function focusOpenedChat(state) {
    const scope = widgetScope();
    if (!scope) return false;

    const message = scope.getElementById(MESSAGE_FIELD_ID);
    if (isVisible(message) && focusElement(message)) return true;

    const preChat = scope.querySelector(
      '[data-testid="userDataModal"] input:not([type="hidden"]), [data-testid="userDataModal"] textarea, input[type="email"]:not([disabled])'
    );
    if (isVisible(preChat) && focusElement(preChat)) return true;

    if (!state.openedChatTab) {
      const tab = chatTab(scope);
      if (isVisible(tab)) {
        state.openedChatTab = true;
        tab.click();
      }
    }

    return false;
  }

  function focusStillOnTrigger() {
    if (!returnButton || !returnButton.isConnected) return false;
    const active = document.activeElement;
    return !active || active === document.body || active === returnButton || active === widgetHost();
  }

  function scheduleFocus(allowHostFallback) {
    const token = ++focusToken;
    const state = { openedChatTab: false };
    let tries = 0;

    const attempt = () => {
      if (token !== focusToken || !focusStillOnTrigger()) return;
      tries += 1;
      if (focusOpenedChat(state)) return;
      if (tries < 30) {
        window.setTimeout(attempt, 100);
        return;
      }

      if (!allowHostFallback) return;
      const host = widgetHost();
      if (host && focusStillOnTrigger()) host.focus();
    };

    attempt();
  }

  function clearClaimTimer() {
    if (!claimTimer) return;
    window.clearTimeout(claimTimer);
    claimTimer = 0;
  }

  function expireHeaderClaim() {
    claimTimer = 0;
    if (!expectingOpen) return;
    expectingOpen = false;
    returnButton = null;
    focusToken += 1;
  }

  function openWhenReady() {
    if (widgetOpen || !returnButton || !tidioReady || expectingOpen || openedByHeader) return;
    const api = chatApi();
    if (!api) return;

    expectingOpen = true;
    api.open();
    clearClaimTimer();
    claimTimer = window.setTimeout(expireHeaderClaim, OPEN_CLAIM_MS);
  }

  const CHAT_OPEN_ATTR = 'data-shelfology-chat-open';
  const TIDIO_HOST_IDS = ['tidio-chat', 'tidio-chat-iframe'];
  let lateHostWatch = null;

  function tidioHosts() {
    return TIDIO_HOST_IDS
      .map((id) => document.getElementById(id))
      .filter(Boolean);
  }

  function watchLateHost() {
    if (!widgetOpen || document.getElementById('tidio-chat-iframe') || lateHostWatch) return;
    lateHostWatch = new MutationObserver(() => {
      if (!widgetOpen || !document.getElementById('tidio-chat-iframe')) return;
      lateHostWatch.disconnect();
      lateHostWatch = null;
      markChatOpen();
    });
    lateHostWatch.observe(document.documentElement, { childList: true, subtree: true });
  }

  function markChatOpen() {
    if (!widgetOpen) return;
    tidioHosts().forEach((host) => {
      if (!host.hasAttribute(CHAT_OPEN_ATTR)) host.setAttribute(CHAT_OPEN_ATTR, '');
    });
    watchLateHost();
  }

  function clearChatOpen() {
    tidioHosts().forEach((host) => host.removeAttribute(CHAT_OPEN_ATTR));
    if (lateHostWatch) {
      lateHostWatch.disconnect();
      lateHostWatch = null;
    }
  }

  function markReady() {
    tidioReady = true;
    openWhenReady();
  }

  function handleWidgetOpen() {
    if (widgetOpen) return;

    widgetOpen = true;
    clearClaimTimer();
    if (!expectingOpen) {
      openedByHeader = false;
      returnButton = null;
      focusToken += 1;
      markChatOpen();
      return;
    }

    expectingOpen = false;
    openedByHeader = true;
    markChatOpen();
    scheduleFocus(true);
  }

  function handleWidgetClose() {
    if (!widgetOpen) return;

    widgetOpen = false;
    clearChatOpen();
    clearClaimTimer();
    focusToken += 1;
    expectingOpen = false;
    const button = returnButton;
    const restore = openedByHeader;
    returnButton = null;
    openedByHeader = false;
    if (!restore || !button || !button.isConnected) return;

    const active = document.activeElement;
    if (!active || active === document.body || active === widgetHost()) {
      button.focus();
    }
  }

  function bindApiEvents(api) {
    if (!api || typeof api.on !== 'function' || apiEventsBound) return;
    apiEventsBound = true;
    api.on('open', handleWidgetOpen);
    api.on('close', handleWidgetClose);
  }

  function requestOpen(button) {
    if (widgetOpen) return;
    returnButton = button;
    openWhenReady();
  }

  if (window.tidioChatApi && typeof window.tidioChatApi.on === 'function') {
    bindApiEvents(window.tidioChatApi);
    window.tidioChatApi.on('ready', markReady);
    document.addEventListener('tidioChat-ready', markReady);
  } else {
    document.addEventListener('tidioChat-ready', () => {
      const api = window.tidioChatApi;
      bindApiEvents(api);
      if (api && typeof api.on === 'function') {
        api.on('ready', markReady);
      }
      markReady();
    });
  }

  document.addEventListener('tidioChat-open', handleWidgetOpen);
  document.addEventListener('tidioChat-close', handleWidgetClose);

  document.addEventListener('click', (event) => {
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;
    requestOpen(button);
  });
})();
